<template>
    <div
        class="slot"
        :style="$options.getStyle(itemSlotsGroupMapping, itemSlotMapping, itemId)"
        @click="$options.handleClick(itemSlotMapping)"
        @contextmenu.prevent="$options.handleRightClick($event, itemSlotMapping)"
    >
        <div class="slot-edge slot-start" v-if="itemSlotMapping.firstOfGroup">
            <div class="spacer"></div>
            <div class="dragger" :style="$options.slotEdgeStyle(itemSlotMapping)"></div>
        </div>
        <div
            class="slot-info"
            :style="$options.slotInfoStyle(itemSlotMapping)"
            :key="'slot-info-' + itemSlotMapping.id"
            :draggable="true"
            @dragstart="$options.handleDragStart($event, itemSlotMapping, itemId)"
            @dragend.prevent="$options.handleDragEnd($event)"
            @mouseover="$options.handleMouseOver($event, itemSlotMapping)"
        >
            <div
                class="titles"
                :style="$options.getTitleStyle(itemSlotMapping)"
                v-if="itemSlotMapping.showTitle"
                v-html="$options.getTitle(itemSlotMapping, itemSlotsGroupMapping)"
            ></div>
        </div>
        <div class="slot-edge slot-end" v-if="itemSlotMapping.lastOfGroup">
            <div class="dragger" :style="$options.slotEdgeStyle(itemSlotMapping)"></div>
            <div class="spacer"></div>
        </div>
    </div>
</template>

<script>
import store from '../../stores';
import { difference } from 'lodash';
import { emitter } from '../../plugins/Mitt';
import {getSlotStyling} from "../../utils/planning/getSlotStyling";

export default {
    props: {
        itemId: {
            type: Number,
            required: true,
        },
        itemSlotMapping: {
            type: Object,
            default() {
                return {};
            },
        },
        itemSlotsGroupMapping: {
            type: Object,
            default() {
                return {};
            },
        },
    },
    getStyle(itemSlotsGroupMapping, itemSlotMapping, itemId) {
        const slotsGroupMapping = itemSlotsGroupMapping[itemSlotMapping.firstSlotId];
        if (!slotsGroupMapping) {
            return {};
        }

        const height = slotsGroupMapping.height;
        const posY = slotsGroupMapping.startYPos;

        const draggedSlot = store.state.planning.dragStartInfo.draggedSlot;
        const mode = store.state.planning.mode;
        let slotItemId = null;
        if (draggedSlot && mode === 'project') {
            slotItemId = draggedSlot.taskId;
        } else if (draggedSlot && mode === 'person') {
            slotItemId = draggedSlot.personId;
        } else if (draggedSlot && mode === 'equipment') {
            slotItemId = draggedSlot.equipmentId;
        } else if (draggedSlot && mode === 'team') {
            slotItemId = draggedSlot.teamId;
        }

        const zIndex = itemSlotMapping.showTitle ? 2 : 1;

        return {
            height: height + 'px',
            transform: 'translateY(' + posY + 'px)',
            zIndex: zIndex,
        };
    },
    getTitle: function (itemsSlotsMapping, itemSlotsGroupMapping) {
        const mode = store.state.planning.mode;

        const timings =
            '<br><small>' +
            itemsSlotsMapping.timeStart +
            ' - ' +
            itemsSlotsMapping.timeEnd +
            '</small>';

        const persons = store.state.planning.persons;
        const slotPersons = itemsSlotsMapping.persons;
        let teamsForSlot = {};
        let personsAmount = 0;
        let hasIncompleteTeam = false;
        Object.values(slotPersons).forEach((person) => {
            if (person.teams.length > 0) {
                person.teams.forEach((team) => {
                    teamsForSlot[team] = team;

                    let teamObject = store.state.planning.teams[team];
                    const amountPersonsMissing = difference(
                        teamObject.persons,
                        Object.keys(slotPersons).map(Number),
                    ).length;
                    if (amountPersonsMissing > 0) {
                        hasIncompleteTeam = true;
                    }
                });
                return;
            }
            personsAmount++;
        });
        const equipments = store.state.planning.equipments;
        const slotEquipments = itemsSlotsMapping.equipments;
        let equipmentsAmount = 0;
        Object.values(slotEquipments).forEach((equipment) => {
            if (equipment.teams.length > 0) {
                equipment.teams.forEach((team) => {
                    teamsForSlot[team] = team;

                    let teamObject = store.state.planning.teams[team];
                    const amountEquipmentsMissing = difference(
                        teamObject.equipments,
                        Object.keys(slotEquipments).map(Number),
                    ).length;
                    if (amountEquipmentsMissing > 0) {
                        hasIncompleteTeam = true;
                    }
                });
                return;
            }
            equipmentsAmount++;
        });

        let subTitle = '';
        const teamsAmount = Object.keys(teamsForSlot).length;
        if (teamsAmount > 0) {
            subTitle +=
                '<i class="icofont-people"></i>' + teamsAmount + (hasIncompleteTeam ? '!' : '');
        }
        if (personsAmount) {
            subTitle += '<i class="icofont-user"></i>' + personsAmount;
        }
        if (equipmentsAmount) {
            subTitle += '<i class="icofont-vehicle-cement"></i>' + equipmentsAmount;
        }

        if (itemsSlotsMapping.repeatedSettingsId) {
            subTitle = '<i class="icofont-retweet"></i>' + subTitle;
        }

        switch (mode) {
            case 'project':
                let teamTitle = '';

                if (teamsAmount > 0) {
                    const slotTeams = itemsSlotsMapping.teams;
                    const team = store.state.planning.teams[Object.keys(slotTeams)[0]];
                    teamTitle = team.title;
                }

                let personTitle = '';
                if (personsAmount > 0) {
                    const person = persons[Object.keys(slotPersons)[0]];
                    personTitle = person.last_name + ' ' + (person.first_name ?? '');
                    personTitle = personTitle.trim();
                }
                let title = teamTitle ? teamTitle : personTitle;

                if (!title) {
                    const task = store.state.planning.tasks[itemsSlotsMapping.taskId];
                    title = task.title;
                }
                return title + '<br>' + subTitle + timings;
            case 'team':
            case 'person':
            case 'equipment':
            default:
                const task = store.state.planning.tasks[itemsSlotsMapping.taskId];
                const taskTitle = task.title;
                const projectTitle = task.project?.title ?? '';
                const projectInternalId = task.project?.internal_id ?? '';
                const client = task.client?.title ?? '';

                const slotTitles = store.state.planning.slotTitles;

                let slotTitle = [];
                Object.keys(slotTitles).forEach((entity) => {
                    if (!slotTitles[entity]) {
                        return;
                    }
                    switch (entity) {
                        case 'task':
                            taskTitle ? slotTitle.push(taskTitle + '<br>') : '';
                            break;
                        case 'project':
                            projectTitle ? slotTitle.push(projectTitle + '<br>') : '';
                            break;
                        case 'project_internal_number':
                            projectInternalId ? slotTitle.push(projectInternalId + '<br>') : '';
                            break;
                        case 'client':
                            client ? slotTitle.push(client + '<br>') : '';
                            break;
                    }
                });
                if (subTitle) {
                    slotTitle.push(subTitle);
                }
                if (timings) {
                    slotTitle.push(timings);
                }

                const slotsGroupMapping = itemSlotsGroupMapping[itemsSlotsMapping.firstSlotId];
                const linesToShow = Math.floor(slotsGroupMapping.height / 15) ?? 1;

                return slotTitle.slice(0, linesToShow).join('');
        }
    },
    getTitleStyle(itemSlotMapping) {
        const slotStyling = getSlotStyling(store, itemSlotMapping);
        return {
            pointerEvents: 'none',
            width: itemSlotMapping.titleWidth + 'px',
            color: slotStyling.color,
        };
    },
    slotInfoStyle(itemSlotMapping) {
        const slotStyling = getSlotStyling(store, itemSlotMapping);
        return {
            backgroundColor: slotStyling.backgroundColor,
            backgroundImage: slotStyling.backgroundImage,
        };
    },
    slotEdgeStyle(itemSlotMapping) {
        const slotStyling = getSlotStyling(store, itemSlotMapping);
        return {
            backgroundColor: slotStyling.darkenBackgroundColor,
        };
    },
    handleDragStart(event, itemSlotMapping, itemId) {
        emitter.emit('hide-slot-hover-info');
        emitter.emit('hide-day-hover-info');
        const body = document.body;
        body.classList.add('cursor-grabbing');

        const dragInfoElement = document.querySelector('.drag-info');
        event.dataTransfer.setDragImage(dragInfoElement, 0, 0);

        let startDrag = {
            slotId: itemSlotMapping.id,
            date: itemSlotMapping.date,
            localDate: itemSlotMapping.localDate,
            firstSlotId: itemSlotMapping.firstSlotId,
            taskId: itemSlotMapping.taskId,
        };

        const mode = store.state.planning.mode;

        if (mode === 'person') {
            startDrag.personId = itemId;
        } else if (mode === 'equipment') {
            startDrag.equipmentId = itemId;
        } else if (mode === 'team') {
            startDrag.teamId = itemId;
        }

        store.commit('planning/setDragStartData', startDrag);
    },
    handleDragEnd($event) {
        document.body.classList.remove('cursor-grabbing');
        setTimeout(() => {
            store.commit('planning/setDragEnd');
        }, 200);
    },
    handleClick(itemSlotMapping) {
        emitter.emit('open-planning-form', {
            slotId: itemSlotMapping.id,
        });
    },
    handleRightClick(event, itemSlotMapping) {
        let eventObject = {
            slot: itemSlotMapping,
        };
        emitter.emit('show-context-menu-component', { eventObject: eventObject, event: event });
    },
    handleMouseOver($event, itemSlotMapping) {
        if (store.state.planning.isDragging) {
            return;
        }
        emitter.emit('show-slot-hover-info', {
            slotId: itemSlotMapping.firstSlotId ?? itemSlotMapping.id,
            event: $event,
        });
    },
};
</script>

<style lang="scss">
.slot {
    width: 100%;
    position: absolute;
    text-align: left;
    color: #fff;
    font-size: 12px;
    line-height: 12px;
    display: flex;
    .slot-start {
        display: flex;
        .dragger {
            border-radius: 5px 0 0 5px;
        }
    }
    .slot-info {
        flex-basis: calc(100% + 2px);
        overflow: visible;
        position: relative;
        margin-left: -1px;
        margin-right: -1px;
    }
    .slot-end {
        display: flex;
        .dragger {
            border-radius: 0 5px 5px 0;
        }
    }
    .slot-edge {
        .dragger {
            width: 5px;
        }
        .spacer {
            width: 5px;
            pointer-events: none;
        }
    }
    &:hover {
        -webkit-filter: brightness(95%);
        filter: brightness(95%);
    }
}
.titles {
    position: absolute;
    z-index: 30;
    padding-left: 5px;
    height: 100%;
    white-space: nowrap;
    overflow: hidden;
    pointer-events: none;
    text-overflow: ellipsis;
    font-size: 12px;
    line-height: 15px;
}
</style>
